import React, { useState, useEffect, useMemo } from "react";

const InsuLeftfilter = ({ onFilterChange, insurancePlans }) => {
  const [filters, setFilters] = useState({
    planType: [],
    sumInsured: [],
    sortBy: [],
    insurer: [],
    coverages: [],
    assistanceServices: [],
    policyStatus: [],
    riders: []
  });

  // Extract unique values from insurance plans for dynamic dropdowns
  const dynamicOptions = useMemo(() => {
    if (!insurancePlans || insurancePlans.length === 0) {
      return {
        planType: [],
        sumInsured: [],
        insurer: [],
        coverages: [],
        assistanceServices: [],
        policyStatus: [],
        riders: []
      };
    }

    // Get unique plan types - use planName or planType
    const planTypes = [...new Set(
      insurancePlans
        .map(plan => {
          // Try to extract meaningful plan type
          if (plan.planType && plan.planType !== 'undefined') return plan.planType;
          if (plan.planName) {
            // Extract plan type from plan name if possible
            const name = plan.planName;
            if (name.includes('Standard')) return 'Standard';
            if (name.includes('Premium')) return 'Premium';
            if (name.includes('Basic')) return 'Basic';
            return name;
          }
          return null;
        })
        .filter(Boolean)
    )].sort();

    // Get unique sum insured values with proper formatting
    const sumInsuredValues = [...new Set(
      insurancePlans
        .map(plan => {
          if (plan.sumInsured) {
            // Extract numeric value and currency
            const value = plan.sumInsured;
            if (typeof value === 'string') {
              const match = value.match(/([A-Za-z]+)\s*([\d,]+)/);
              if (match) {
                return `${match[1]} ${match[2]}`;
              }
              return value;
            }
            return `₹${value.toLocaleString()}`;
          }
          return null;
        })
        .filter(Boolean)
    )].sort((a, b) => {
      const numA = parseFloat(a.replace(/[^0-9.]/g, ''));
      const numB = parseFloat(b.replace(/[^0-9.]/g, ''));
      return numA - numB;
    });

    // Get unique insurers
    const insurers = [...new Set(
      insurancePlans
        .map(plan => plan.insurerName)
        .filter(Boolean)
    )].sort();

    // Get unique coverages with max amounts
    const coverageSet = new Map();
    insurancePlans.forEach(plan => {
      if (plan.coverages && Array.isArray(plan.coverages)) {
        plan.coverages.forEach(coverage => {
          if (coverage.name) {
            const key = coverage.name;
            const value = coverage.maxAmount ? `${coverage.name} (${coverage.maxAmount})` : coverage.name;
            coverageSet.set(key, value);
          }
        });
      }
    });
    const coverages = [...coverageSet.values()].sort();

    // Get unique assistance services
    const assistanceSet = new Set();
    insurancePlans.forEach(plan => {
      if (plan.assistantServices && Array.isArray(plan.assistantServices)) {
        plan.assistantServices.forEach(service => {
          if (service.name) {
            assistanceSet.add(service.name);
          }
        });
      }
    });
    const assistanceServices = [...assistanceSet].sort();

    // Get unique policy status
    const policyStatus = [...new Set(
      insurancePlans
        .map(plan => plan.policyStatus)
        .filter(Boolean)
    )].sort();

    // Get unique riders
    const riderSet = new Set();
    insurancePlans.forEach(plan => {
      if (plan.riders && Array.isArray(plan.riders)) {
        plan.riders.forEach(rider => {
          if (rider.name) {
            const riderName = rider.premiumPercent ?
              `${rider.name} (${rider.premiumPercent}%)` :
              rider.name;
            riderSet.add(riderName);
          }
        });
      }
    });
    const riders = [...riderSet].sort();

    return {
      planType: planTypes,
      sumInsured: sumInsuredValues,
      insurer: insurers,
      coverages: coverages,
      assistanceServices: assistanceServices,
      policyStatus: policyStatus,
      riders: riders
    };
  }, [insurancePlans]);

  // Handle checkbox change
  const handleCheckboxChange = (category, value, checked) => {
    setFilters(prev => {
      const updatedCategory = checked
        ? [...prev[category], value]
        : prev[category].filter(item => item !== value);

      const newFilters = { ...prev, [category]: updatedCategory };

      if (onFilterChange) {
        onFilterChange(newFilters);
      }

      return newFilters;
    });
  };

  // Reset all filters
  const resetFilters = () => {
    const resetState = {
      planType: [],
      sumInsured: [],
      sortBy: [],
      insurer: [],
      coverages: [],
      assistanceServices: [],
      policyStatus: [],
      riders: []
    };
    setFilters(resetState);
    if (onFilterChange) {
      onFilterChange(resetState);
    }
  };

  // Clear specific filter category
  const clearCategory = (category) => {
    setFilters(prev => {
      const newFilters = { ...prev, [category]: [] };
      if (onFilterChange) {
        onFilterChange(newFilters);
      }
      return newFilters;
    });
  };

  // Check if any filter is active
  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(arr => arr.length > 0);
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    return Object.values(filters).reduce((sum, arr) => sum + arr.length, 0);
  }, [filters]);

  return (
    <div className="box_shadow">
      <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
        <h6 className="mb-0 fw-bold">Filters</h6>
        {hasActiveFilters && (
          <button
            className="btn btn-sm btn-outline-danger"
            onClick={resetFilters}
          >
            Reset All ({activeFilterCount})
          </button>
        )}
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="p-2 bg-light border-bottom">
          <div className="d-flex flex-wrap gap-1">
            {Object.entries(filters).map(([category, values]) =>
              values.map(value => (
                <span key={`${category}-${value}`} className="badge d-flex align-items-center gap-1">
                  {value}
                  <span
                    className="ms-1"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      setFilters(prev => {
                        const newFilters = { ...prev, [category]: prev[category].filter(v => v !== value) };
                        if (onFilterChange) {
                          onFilterChange(newFilters);
                        }
                        return newFilters;
                      });
                    }}
                  >
                    ×
                  </span>
                </span>
              ))
            )}
          </div>
        </div>
      )}

      <div className="accordion" id="filterAccordion">
        {/* Plan Type */}
        {dynamicOptions.planType.length > 0 && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button className="accordion-button fs-5 fw-semibold collapsed" type="button"
                data-bs-toggle="collapse" data-bs-target="#collapse-Plan" aria-expanded="false">
                Plan Type
                {filters.planType.length > 0 && (
                  <span className="badge ms-2">{filters.planType.length}</span>
                )}
              </button>
            </h2>
            <div id="collapse-Plan" className="accordion-collapse collapse">
              <div className="accordion-body">
                {filters.planType.length > 0 && (
                  <button
                    className="btn-sm btn-link text-danger p-0 mb-2"
                    onClick={() => clearCategory('planType')}
                  >
                    Clear all
                  </button>
                )}
                {dynamicOptions.planType.map((type, index) => (
                  <div className="form-check w-100" key={`plan-${index}`}>
                    <input
                      className="form-check-input filter-category-planType"
                      type="checkbox"
                      id={`plan-${index}`}
                      checked={filters.planType.includes(type)}
                      onChange={(e) => handleCheckboxChange('planType', type, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`plan-${index}`}>{type}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Sum Insured */}
        {dynamicOptions.sumInsured.length > 0 && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button className="accordion-button fs-5 fw-semibold collapsed" type="button" data-bs-toggle="collapse"
                data-bs-target="#collapse-Sum" aria-expanded="false">
                Sum Insured
                {filters.sumInsured.length > 0 && (
                  <span className="badge ms-2">{filters.sumInsured.length}</span>
                )}
              </button>
            </h2>
            <div id="collapse-Sum" className="accordion-collapse collapse">
              <div className="accordion-body">
                {filters.sumInsured.length > 0 && (
                  <button
                    className="btn-sm btn-link text-danger p-0 mb-2"
                    onClick={() => clearCategory('sumInsured')}
                  >
                    Clear all
                  </button>
                )}
                {dynamicOptions.sumInsured.map((value, index) => (
                  <div className="form-check w-100" key={`sum-${index}`}>
                    <input
                      className="form-check-input filter-category-sumInsured"
                      type="checkbox"
                      id={`sum-${index}`}
                      checked={filters.sumInsured.includes(value)}
                      onChange={(e) => handleCheckboxChange('sumInsured', value, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`sum-${index}`}>{value}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Sort by - Static */}
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button className="accordion-button fs-5 fw-semibold collapsed" type="button" data-bs-toggle="collapse"
              data-bs-target="#collapse-Sort" aria-expanded="false">
              Sort by
              {filters.sortBy.length > 0 && (
                <span className="badge ms-2">{filters.sortBy.length}</span>
              )}
            </button>
          </h2>
          <div id="collapse-Sort" className="accordion-collapse collapse">
            <div className="accordion-body">
              {filters.sortBy.length > 0 && (
                <button
                  className="btn-sm btn-link text-danger p-0 mb-2"
                  onClick={() => clearCategory('sortBy')}
                >
                  Clear all
                </button>
              )}
              <div className="form-check w-100">
                <input
                  className="form-check-input filter-category-sortBy"
                  type="checkbox"
                  checked={filters.sortBy.includes('Default')}
                  onChange={(e) => handleCheckboxChange('sortBy', 'Default', e.target.checked)}
                />
                <label className="form-check-label">Default</label>
              </div>
              <div className="form-check w-100">
                <input
                  className="form-check-input filter-category-sortBy"
                  type="checkbox"
                  checked={filters.sortBy.includes('Premium low to high')}
                  onChange={(e) => handleCheckboxChange('sortBy', 'Premium low to high', e.target.checked)}
                />
                <label className="form-check-label">Premium low to high</label>
              </div>
              <div className="form-check w-100">
                <input
                  className="form-check-input filter-category-sortBy"
                  type="checkbox"
                  checked={filters.sortBy.includes('Premium high to low')}
                  onChange={(e) => handleCheckboxChange('sortBy', 'Premium high to low', e.target.checked)}
                />
                <label className="form-check-label">Premium high to low</label>
              </div>
            </div>
          </div>
        </div>

        {/* Insurer */}
        {dynamicOptions.insurer.length > 0 && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button className="accordion-button fs-5 fw-semibold collapsed" type="button" data-bs-toggle="collapse"
                data-bs-target="#collapse-Insurer" aria-expanded="false">
                Insurer
                {filters.insurer.length > 0 && (
                  <span className="badge ms-2">{filters.insurer.length}</span>
                )}
              </button>
            </h2>
            <div id="collapse-Insurer" className="accordion-collapse collapse">
              <div className="accordion-body">
                {filters.insurer.length > 0 && (
                  <button
                    className="btn-sm btn-link text-danger p-0 mb-2"
                    onClick={() => clearCategory('insurer')}
                  >
                    Clear all
                  </button>
                )}
                {dynamicOptions.insurer.map((insurer, index) => (
                  <div className="form-check w-100" key={`insurer-${index}`}>
                    <input
                      className="form-check-input filter-category-insurer"
                      type="checkbox"
                      id={`insurer-${index}`}
                      checked={filters.insurer.includes(insurer)}
                      onChange={(e) => handleCheckboxChange('insurer', insurer, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`insurer-${index}`}>{insurer}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Coverages */}
        {dynamicOptions.coverages.length > 0 && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button className="accordion-button fs-5 fw-semibold collapsed" type="button" data-bs-toggle="collapse"
                data-bs-target="#collapse-Coverages" aria-expanded="false">
                Coverages
                {filters.coverages.length > 0 && (
                  <span className="badge ms-2">{filters.coverages.length}</span>
                )}
              </button>
            </h2>
            <div id="collapse-Coverages" className="accordion-collapse collapse">
              <div className="accordion-body">
                {filters.coverages.length > 0 && (
                  <button
                    className="btn-sm btn-link text-danger p-0 mb-2"
                    onClick={() => clearCategory('coverages')}
                  >
                    Clear all
                  </button>
                )}
                {dynamicOptions.coverages.map((coverage, index) => (
                  <div className="form-check w-100" key={`coverage-${index}`}>
                    <input
                      className="form-check-input filter-category-coverages"
                      type="checkbox"
                      id={`coverage-${index}`}
                      checked={filters.coverages.includes(coverage)}
                      onChange={(e) => handleCheckboxChange('coverages', coverage, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`coverage-${index}`}>{coverage}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Assistance Services */}
        {dynamicOptions.assistanceServices.length > 0 && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button className="accordion-button fs-5 fw-semibold collapsed" type="button" data-bs-toggle="collapse"
                data-bs-target="#collapse-Assistance" aria-expanded="false">
                Assistance Services
                {filters.assistanceServices.length > 0 && (
                  <span className="badge ms-2">{filters.assistanceServices.length}</span>
                )}
              </button>
            </h2>
            <div id="collapse-Assistance" className="accordion-collapse collapse">
              <div className="accordion-body">
                {filters.assistanceServices.length > 0 && (
                  <button
                    className="btn-sm btn-link text-danger p-0 mb-2"
                    onClick={() => clearCategory('assistanceServices')}
                  >
                    Clear all
                  </button>
                )}
                {dynamicOptions.assistanceServices.map((service, index) => (
                  <div className="form-check w-100" key={`assistance-${index}`}>
                    <input
                      className="form-check-input filter-category-assistanceServices"
                      type="checkbox"
                      id={`assistance-${index}`}
                      checked={filters.assistanceServices.includes(service)}
                      onChange={(e) => handleCheckboxChange('assistanceServices', service, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`assistance-${index}`}>{service}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Policy Status */}
        {dynamicOptions.policyStatus.length > 0 && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button className="accordion-button fs-5 fw-semibold collapsed" type="button" data-bs-toggle="collapse"
                data-bs-target="#collapse-Status" aria-expanded="false">
                Policy Status
                {filters.policyStatus.length > 0 && (
                  <span className="badge ms-2">{filters.policyStatus.length}</span>
                )}
              </button>
            </h2>
            <div id="collapse-Status" className="accordion-collapse collapse">
              <div className="accordion-body">
                {filters.policyStatus.length > 0 && (
                  <button
                    className="btn-sm btn-link text-danger p-0 mb-2"
                    onClick={() => clearCategory('policyStatus')}
                  >
                    Clear all
                  </button>
                )}
                {dynamicOptions.policyStatus.map((status, index) => (
                  <div className="form-check w-100" key={`status-${index}`}>
                    <input
                      className="form-check-input filter-category-policyStatus"
                      type="checkbox"
                      id={`status-${index}`}
                      checked={filters.policyStatus.includes(status)}
                      onChange={(e) => handleCheckboxChange('policyStatus', status, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`status-${index}`}>{status}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Riders */}
        {dynamicOptions.riders.length > 0 && (
          <div className="accordion-item">
            <h2 className="accordion-header">
              <button className="accordion-button fs-5 fw-semibold collapsed" type="button" data-bs-toggle="collapse"
                data-bs-target="#collapse-Riders" aria-expanded="false">
                Riders
                {filters.riders.length > 0 && (
                  <span className="badge ms-2">{filters.riders.length}</span>
                )}
              </button>
            </h2>
            <div id="collapse-Riders" className="accordion-collapse collapse">
              <div className="accordion-body">
                {filters.riders.length > 0 && (
                  <button
                    className="btn-sm btn-link text-danger p-0 mb-2"
                    onClick={() => clearCategory('riders')}
                  >
                    Clear all
                  </button>
                )}
                {dynamicOptions.riders.map((rider, index) => (
                  <div className="form-check w-100" key={`rider-${index}`}>
                    <input
                      className="form-check-input filter-category-riders"
                      type="checkbox"
                      id={`rider-${index}`}
                      checked={filters.riders.includes(rider)}
                      onChange={(e) => handleCheckboxChange('riders', rider, e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor={`rider-${index}`}>{rider}</label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InsuLeftfilter;